import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Badge, Group, Select, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { getReport, listOrganizations, listReports, listUsers, type ListParams } from '../../api/resources';
import { CopyableId } from '../../components/CopyableText';
import { DataPanel } from '../../components/DataPanel';
import { DetailDrawer } from '../../components/detail/DetailDrawer';
import { DetailField, DetailFieldGrid } from '../../components/detail/DetailField';
import { DetailSection } from '../../components/detail/DetailSection';
import { KeySignalsSummary } from '../../components/detail/facts/KeySignalsSummary';
import { ReportSignalsSection } from '../../components/detail/ReportSignalsSection';
import { EntityRef } from '../../components/EntityRef';
import { JsonBlock } from '../../components/JsonBlock';
import { PageHeader } from '../../components/PageHeader';
import { PaginatedTable } from '../../components/PaginatedTable';
import { reportLabel, reportSubmissionLabel } from '../../lib/reportLabel';
import { useAdminLookups } from '../../hooks/useAdminLookups';
import { DEFAULT_PAGE_SIZE } from '../../hooks/useListQuery';
import type { Report } from '../../types/api';
import { REPORT_SUBMISSION_FILTER_VALUES, REPORT_WORKFLOW_TYPE_VALUES } from '../../types/domain';

function ReportDetailPanel({ reportId }: { reportId: string }) {
    const { t } = useTranslation();
    const lookups = useAdminLookups({ includeVisits: true, includeCases: true });

    const reportQ = useQuery({
        queryKey: ['report', reportId],
        queryFn: () => getReport(reportId),
    });

    const report = reportQ.data;
    if (!report) {
        return null;
    }

    const visit = lookups.resolveVisit(report.visitId, report.visit);
    const empty = t('common.empty');

    return (
        <>
            <DetailFieldGrid>
                <DetailField
                    label={t('fields.visit')}
                    value={
                        <EntityRef
                            title={visit.title}
                            id={report.visitId}
                            href={`/visits/${report.visitId}`}
                        />
                    }
                />
                <DetailField
                    label={t('fields.workflowType')}
                    value={
                        report.workflowType
                            ? t(`visitTypes.${report.workflowType}`, { defaultValue: report.workflowType })
                            : empty
                    }
                />
                <DetailField
                    label={t('fields.submissionState')}
                    value={
                        report.submittedAt ? (
                            <Badge variant="light" color="green">
                                {t('submissionStates.submitted')}
                            </Badge>
                        ) : (
                            <Badge variant="light">{t('submissionStates.draft')}</Badge>
                        )
                    }
                />
                <DetailField label={t('fields.summary')} value={report.summary ?? empty} />
                <DetailField label={t('fields.submitted')} value={reportSubmissionLabel(report)} />
                <DetailField label={t('fields.reportId')} value={<CopyableId id={report.id} />} />
            </DetailFieldGrid>
            <KeySignalsSummary signals={report.signals ?? []} />
            <ReportSignalsSection signals={report.signals ?? []} />
            {report.data && Object.keys(report.data).length > 0 ? (
                <DetailSection title={t('detail.reportData')}>
                    <JsonBlock value={report.data} />
                </DetailSection>
            ) : null}
        </>
    );
}

export function ReportsPage() {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [page, setPage] = useState(1);
    const [orgFilter, setOrgFilter] = useState<string | null>(null);
    const [officerFilter, setOfficerFilter] = useState<string | null>(null);
    const [workflowFilter, setWorkflowFilter] = useState<string | null>(null);
    const [submittedFilter, setSubmittedFilter] = useState<string | null>(null);
    const detailReportId = searchParams.get('id');
    const lookups = useAdminLookups({ includeVisits: true });

    const orgsQ = useQuery({
        queryKey: ['organizations-all'],
        queryFn: () => listOrganizations({ page: 1, limit: 500 }),
    });
    const usersQ = useQuery({
        queryKey: ['users-field-officers'],
        queryFn: () => listUsers({ page: 1, limit: 500 }),
    });

    const listParams = useMemo(
        (): ListParams => ({
            page,
            limit: DEFAULT_PAGE_SIZE,
            sort: JSON.stringify([{ field: 'createdAt', order: 'DESC' }]),
            ...(orgFilter ? { organizationId: orgFilter } : {}),
            ...(officerFilter ? { fieldOfficerId: officerFilter } : {}),
            ...(workflowFilter ? { workflowType: workflowFilter } : {}),
            ...(submittedFilter ? { submitted: submittedFilter } : {}),
        }),
        [page, orgFilter, officerFilter, workflowFilter, submittedFilter],
    );

    const reportsQ = useQuery({
        queryKey: ['reports', listParams],
        queryFn: () => listReports(listParams),
    });

    const fieldOfficers = (usersQ.data?.data ?? []).filter((u) => !u.isPlatformAdmin);
    const orgOptions = (orgsQ.data?.data ?? []).map((o) => ({ value: o.id, label: o.name }));

    return (
        <Stack gap="lg">
            <PageHeader
                title={t('features.reports.title')}
                description={t('features.reports.description')}
            />
            <DataPanel>
                <Group grow align="flex-end">
                    <Select
                        label={t('fields.organization')}
                        placeholder={t('common.all')}
                        clearable
                        searchable
                        data={orgOptions}
                        value={orgFilter}
                        onChange={(v) => {
                            setOrgFilter(v);
                            setPage(1);
                        }}
                    />
                    <Select
                        label={t('fields.fieldOfficer')}
                        placeholder={t('common.all')}
                        clearable
                        searchable
                        data={fieldOfficers.map((u) => ({
                            value: u.id,
                            label: `${u.firstName} ${u.lastName} (${u.email ?? u.id})`,
                        }))}
                        value={officerFilter}
                        onChange={(v) => {
                            setOfficerFilter(v);
                            setPage(1);
                        }}
                    />
                    <Select
                        label={t('fields.workflowType')}
                        placeholder={t('common.all')}
                        clearable
                        searchable
                        data={REPORT_WORKFLOW_TYPE_VALUES.map((workflowType) => ({
                            value: workflowType,
                            label: t(`visitTypes.${workflowType}`, { defaultValue: workflowType }),
                        }))}
                        value={workflowFilter}
                        onChange={(v) => {
                            setWorkflowFilter(v);
                            setPage(1);
                        }}
                    />
                    <Select
                        label={t('fields.submissionState')}
                        placeholder={t('common.all')}
                        clearable
                        data={REPORT_SUBMISSION_FILTER_VALUES.map((state) => ({
                            value: state,
                            label: t(`submissionStates.${state}`),
                        }))}
                        value={submittedFilter}
                        onChange={(v) => {
                            setSubmittedFilter(v);
                            setPage(1);
                        }}
                    />
                </Group>
            </DataPanel>
            <PaginatedTable
                rowKey={(r: Report) => r.id}
                columns={[
                    {
                        key: 'workflow',
                        header: t('fields.workflowType'),
                        render: (r) =>
                            r.workflowType
                                ? t(`visitTypes.${r.workflowType}`, { defaultValue: r.workflowType })
                                : t('common.empty'),
                    },
                    {
                        key: 'visit',
                        header: t('fields.visit'),
                        render: (r) => {
                            const v = lookups.resolveVisit(r.visitId, r.visit);
                            return (
                                <EntityRef
                                    title={v.title}
                                    id={r.visitId}
                                    href={`/visits/${r.visitId}`}
                                />
                            );
                        },
                    },
                    {
                        key: 'submitted',
                        header: t('fields.submitted'),
                        render: (r) => <Text size="sm">{reportSubmissionLabel(r)}</Text>,
                    },
                    {
                        key: 'report',
                        header: t('fields.report'),
                        render: (r) => (
                            <EntityRef
                                title={reportLabel(r)}
                                id={r.id}
                                href={`/reports?id=${r.id}`}
                            />
                        ),
                    },
                ]}
                rows={reportsQ.data?.data ?? []}
                total={reportsQ.data?.total ?? 0}
                page={page}
                pageSize={DEFAULT_PAGE_SIZE}
                onPageChange={setPage}
                isLoading={reportsQ.isLoading}
                onRowClick={(r) => setSearchParams({ id: r.id })}
            />
            <DetailDrawer
                opened={Boolean(detailReportId)}
                onClose={() => {
                    searchParams.delete('id');
                    setSearchParams(searchParams);
                }}
                title={t('detail.report')}
                subtitle={detailReportId ?? undefined}
            >
                {detailReportId ? <ReportDetailPanel reportId={detailReportId} /> : null}
            </DetailDrawer>
        </Stack>
    );
}
